import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import Badge from "@mui/material/Badge";
import AddIcon from "@mui/icons-material/Add";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";

import UserAvatar from "../management/UserAvatar";
import ConversationGroupAvatar from "./ConversationGroupAvatar";

function getLastMessage(conversation) {
    const messages = conversation.messages || [];
    return messages.length > 0 ? messages[messages.length - 1].message : "";
}

function ConversationAvatar({ conversation }) {
    const avatar =
        conversation.otherUsers.length === 1 ? (
            <UserAvatar
                userOrHub={conversation.otherUsers[0]}
                path="users"
                variant="circular"
            />
        ) : (
            <ConversationGroupAvatar conversation={conversation} />
        );

    return (
        <Badge
            badgeContent={conversation.unreadMessagesCount}
            color="secondary"
            overlap="circular"
            sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                "& .MuiAvatar-root": {
                    width: 40,
                    height: 40,
                },
                "& .MuiBadge-badge": {
                    minWidth: 19,
                    height: 19,
                    px: 0.5,
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    border: "2px solid",
                    borderColor: "background.paper",
                },
            }}
        >
            {avatar}
        </Badge>
    );
}

function ConversationListLoading() {
    return (
        <Box role="status" aria-label="Loading conversations">
            {[0, 1, 2].map((index) => (
                <Box
                    key={index}
                    sx={{
                        height: 68,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.35,
                        px: 1.25,
                    }}
                >
                    <Skeleton
                        variant="circular"
                        width={40}
                        height={40}
                        sx={{ flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="58%" height={22} />
                        <Skeleton variant="text" width="78%" height={18} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

export default function ConversationList(props) {
    const activeConversations = props.conversations.filter(
        (conversation) => conversation.deleted === false,
    );

    const handleConversationClick = (conversation) => () => {
        props.handleSelect(conversation);
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1.5,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" component="h1">
                            Messages
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.35 }}
                        >
                            {props.loading
                                ? "Loading conversations…"
                                : activeConversations.length === 1
                                  ? "1 conversation"
                                  : `${activeConversations.length} conversations`}
                        </Typography>
                    </Box>
                    {props.canAdd && (
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={props.handleAddClick}
                            startIcon={<AddIcon />}
                            sx={{
                                flexShrink: 0,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 700,
                            }}
                        >
                            New
                        </Button>
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    mx: 2.5,
                    height: "1px",
                    flexShrink: 0,
                    backgroundColor: "divider",
                }}
            />

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    scrollbarWidth: "thin",
                }}
            >
                <List
                    disablePadding
                    sx={{
                        display: "block",
                        px: 1.25,
                        py: 1.25,
                    }}
                >
                {props.loading && <ConversationListLoading />}

                {!props.loading && activeConversations.map((conversation) => {
                    const isSelected =
                        props.selected &&
                        props.selected.key === conversation.key;
                    const preview = getLastMessage(conversation);

                    return (
                        <ListItemButton
                            key={conversation.key}
                            selected={isSelected}
                            onClick={handleConversationClick(conversation)}
                            sx={{
                                mb: 0.5,
                                width: "100%",
                                height: 68,
                                minHeight: 68,
                                maxHeight: 68,
                                flex: "none",
                                px: 1.25,
                                py: 1.15,
                                gap: 1.35,
                                alignItems: "center",
                                borderRadius: 2.25,
                                border: "1px solid transparent",
                                "&.Mui-selected": {
                                    borderColor: "rgba(0, 149, 61, 0.2)",
                                    backgroundColor:
                                        "rgba(0, 149, 61, 0.085)",
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor:
                                        "rgba(0, 149, 61, 0.115)",
                                },
                            }}
                        >
                            <Box sx={{ flexShrink: 0, display: "flex" }}>
                                <ConversationAvatar
                                    conversation={conversation}
                                />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                    variant="subtitle2"
                                    noWrap
                                    sx={{
                                        fontWeight:
                                            conversation.unreadMessagesCount > 0
                                                ? 800
                                                : 700,
                                    }}
                                >
                                    {conversation.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    noWrap
                                    color="text.secondary"
                                    sx={{
                                        mt: 0.2,
                                        fontWeight:
                                            conversation.unreadMessagesCount > 0
                                                ? 600
                                                : 400,
                                    }}
                                >
                                    {preview || "No messages yet"}
                                </Typography>
                            </Box>
                            {conversation.unreadMessagesCount > 0 && (
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        flexShrink: 0,
                                        borderRadius: "50%",
                                        backgroundColor: "primary.main",
                                    }}
                                />
                            )}
                        </ListItemButton>
                    );
                })}

                {!props.loading && activeConversations.length === 0 && (
                    <Box
                        sx={{
                            minHeight: 280,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 3,
                            textAlign: "center",
                            color: "text.secondary",
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                display: "grid",
                                placeItems: "center",
                                mb: 1.25,
                                borderRadius: 2.25,
                                color: "primary.main",
                                backgroundColor: "rgba(0, 149, 61, 0.08)",
                            }}
                        >
                            <ForumOutlinedIcon />
                        </Box>
                        <Typography variant="subtitle2" color="text.primary">
                            No conversations yet
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.35 }}>
                            Start a conversation to connect with someone in your
                            hubs.
                        </Typography>
                    </Box>
                )}
                </List>
            </Box>
        </Box>
    );
}
